import { KeyRound } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  clearQueuedPostSignupApiKeyPrompt,
  hasQueuedPostSignupApiKeyPrompt,
} from "@/lib/post-signup-api-key-prompt";

/**
 * Offers the newly signed-up user a direct path to API Keys while preserving
 * Dashboard as the default landing page.
 *
 * @param onCreateApiKey - Navigates from Dashboard to the API Keys tab.
 */
export function PostSignupApiKeyPrompt({
  onCreateApiKey,
}: {
  onCreateApiKey: () => void;
}) {
  const [open, setOpen] = useState(hasQueuedPostSignupApiKeyPrompt);

  const closePrompt = () => {
    clearQueuedPostSignupApiKeyPrompt();
    setOpen(false);
  };

  const openApiKeys = () => {
    closePrompt();
    onCreateApiKey();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) closePrompt();
      }}
    >
      <DialogContent className="border-border/70 bg-background sm:max-w-md">
        <DialogHeader>
          <div className="mb-1 flex size-9 items-center justify-center rounded-full bg-primary/12 text-primary">
            <KeyRound className="size-4" />
          </div>
          <DialogTitle className="text-xl font-medium tracking-tight">
            Create your first API key
          </DialogTitle>
          <DialogDescription className="leading-6">
            Your organization is ready. Create an API key to start building with
            Sully.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-1">
          <Button type="button" variant="outline" onClick={closePrompt}>
            Not now
          </Button>
          <Button type="button" onClick={openApiKeys}>
            Create API key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
